/**
 * Serviço de Cache Client-Side
 * Replica a lógica de cache do PHP (24h para perfis, 1h para posts/followers)
 */

const CACHE_KEYS = {
  PROFILE: 'instagram_cache_profile_',
  FOLLOWERS: 'instagram_cache_followers_',
  POSTS: 'instagram_cache_posts_',
  FULL_DATA: 'instagram_cache_full_'
};

const CACHE_TTL = {
  profile: 24 * 60 * 60 * 1000,  // 24 horas
  followers: 60 * 60 * 1000,     // 1 hora
  posts: 60 * 60 * 1000,         // 1 hora
  full: 60 * 60 * 1000           // 1 hora (dados completos)
};

/**
 * Verifica se o localStorage está disponível
 */
function isLocalStorageAvailable() {
  try {
    const test = '__localStorage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
}

/**
 * Classe de gerenciamento de cache
 */
class CacheService {
  constructor() {
    this.enabled = isLocalStorageAvailable();
    if (!this.enabled) {
      console.warn('localStorage não disponível - cache desabilitado');
    }
  }

  /**
   * Salva dados no cache
   */
  set(key, data, ttl = CACHE_TTL.full) {
    if (!this.enabled) return false;

    try {
      const cacheItem = {
        data,
        timestamp: Date.now(),
        expiresAt: Date.now() + ttl
      };
      localStorage.setItem(key, JSON.stringify(cacheItem));
      return true;
    } catch (error) {
      console.error('Erro ao salvar no cache:', error);
      // Se localStorage estiver cheio, limpar cache antigo
      if (error.name === 'QuotaExceededError') {
        this.clearOldCache();
        try {
          const cacheItem = {
            data,
            timestamp: Date.now(),
            expiresAt: Date.now() + ttl
          };
          localStorage.setItem(key, JSON.stringify(cacheItem));
          return true;
        } catch {
          return false;
        }
      }
      return false;
    }
  }

  /**
   * Recupera dados do cache
   */
  get(key) {
    if (!this.enabled) return null;

    try {
      const cached = localStorage.getItem(key);
      if (!cached) return null;

      const cacheItem = JSON.parse(cached);
      
      // Verificar se expirou
      if (Date.now() > cacheItem.expiresAt) {
        localStorage.removeItem(key);
        return null;
      }

      return cacheItem.data;
    } catch (error) {
      console.error('Erro ao ler cache:', error);
      return null;
    }
  }

  /**
   * Remove item do cache
   */
  remove(key) {
    if (!this.enabled) return;
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Erro ao remover do cache:', error);
    }
  }

  /**
   * Limpa cache expirado
   */
  clearOldCache() {
    if (!this.enabled) return;

    try {
      const now = Date.now();
      const keysToRemove = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key || !key.startsWith('instagram_cache_')) continue;

        try {
          const cached = localStorage.getItem(key);
          if (!cached) continue;

          const cacheItem = JSON.parse(cached);
          if (now > cacheItem.expiresAt) {
            keysToRemove.push(key);
          }
        } catch {
          keysToRemove.push(key); // Remover itens corrompidos
        }
      }

      keysToRemove.forEach(key => localStorage.removeItem(key));
      console.log(`Cache limpo: ${keysToRemove.length} itens removidos`);
    } catch (error) {
      console.error('Erro ao limpar cache:', error);
    }
  }

  /**
   * Limpa todo o cache do Instagram
   */
  clearAll() {
    if (!this.enabled) return;

    try {
      const keys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('instagram_cache_')) {
          keys.push(key);
        }
      }
      keys.forEach(key => localStorage.removeItem(key));
      console.log(`Cache completo limpo: ${keys.length} itens`);
    } catch (error) {
      console.error('Erro ao limpar cache completo:', error);
    }
  }

  /**
   * Salva dados completos de perfil (API response completa)
   */
  setFullProfile(username, data) {
    const key = CACHE_KEYS.FULL_DATA + username.toLowerCase();
    return this.set(key, data, CACHE_TTL.full);
  }

  /**
   * Recupera dados completos de perfil
   */
  getFullProfile(username) {
    const key = CACHE_KEYS.FULL_DATA + username.toLowerCase();
    return this.get(key);
  }

  /**
   * Remove dados de perfil específico
   */
  removeProfile(username) {
    const cleanUsername = username.toLowerCase();
    this.remove(CACHE_KEYS.FULL_DATA + cleanUsername);
    this.remove(CACHE_KEYS.PROFILE + cleanUsername);
    this.remove(CACHE_KEYS.FOLLOWERS + cleanUsername);
    this.remove(CACHE_KEYS.POSTS + cleanUsername);
  }

  /**
   * Obtém informações de uso do cache
   */
  getCacheInfo() {
    if (!this.enabled) {
      return {
        enabled: false,
        count: 0,
        size: 0
      };
    }

    try {
      let count = 0;
      let size = 0;

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('instagram_cache_')) {
          count++;
          const value = localStorage.getItem(key);
          if (value) {
            size += new Blob([value]).size;
          }
        }
      }

      return {
        enabled: true,
        count,
        size: (size / 1024).toFixed(2) + ' KB',
        sizeBytes: size
      };
    } catch (error) {
      console.error('Erro ao obter info do cache:', error);
      return {
        enabled: true,
        count: 0,
        size: 0
      };
    }
  }
}

// Exportar instância única (singleton)
const cacheService = new CacheService();

// Limpar cache expirado ao inicializar
if (cacheService.enabled) {
  cacheService.clearOldCache();
}

// Mapa de requests em andamento para deduplicação
const pendingRequests = {};

/**
 * Busca o perfil com cache (localStorage) + deduplicação de requests simultâneos.
 * Se dois componentes chamarem ao mesmo tempo, só 1 fetch é feito.
 */
export async function fetchProfileCached(username) {
  if (!username) return null;
  const key = username.toLowerCase();

  // 1. Cache localStorage
  const cached = cacheService.getFullProfile(key);
  if (cached) {
    console.log('✅ [cache] Perfil carregado do cache:', key);
    return { success: true, data: cached };
  }

  // 2. Request já em andamento → reutiliza a mesma promise
  if (pendingRequests[key]) {
    console.log('⏳ [cache] Aguardando request já em andamento para:', key);
    return pendingRequests[key];
  }

  // 3. Novo fetch
  const promise = fetch(`/api/search-profile?username=${encodeURIComponent(key)}`)
    .then(res => {
      if (!res.ok) throw new Error('API error');
      return res.json();
    })
    .then(result => {
      if (result.success && result.data) {
        cacheService.setFullProfile(key, result.data);
        console.log('💾 [cache] Perfil salvo no cache:', key);
      }
      return result;
    })
    .finally(() => {
      delete pendingRequests[key];
    });

  pendingRequests[key] = promise;
  return promise;
}

export default cacheService;
